import { Box, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Loader } from '@/components'
import { useAppContext } from '@/hooks'
import { ResizableSidebarLayout } from '@/pages/User/components/ResizableSidebarLayout'
import { TreePanelV2 } from '@/pages/User/pages/Home/components/TreePanel/TreePanelV2'

import { CreatePackageDialog } from './CreatePackageDialog'
import { PackageDetail } from './PackageDetail'
import { PackageList } from './PackageList'
import { getDeploymentPackagePath } from './types'
import { useDeploymentPackages } from './useDeploymentPackages'

export const DeploymentPackagesLayout = () => {
	const { t } = useAppContext()
	const navigate = useNavigate()
	const { packageId } = useParams<{ packageId?: string }>()
	const { hasSystemId, isError, isLoading, packages, systemId } = useDeploymentPackages()
	const [createOpen, setCreateOpen] = useState(false)
	const previousSystemId = useRef(systemId)

	useEffect(() => {
		if (previousSystemId.current === systemId) return
		const hadSystem = previousSystemId.current != null
		previousSystemId.current = systemId
		if (hadSystem && packageId) navigate(getDeploymentPackagePath())
	}, [navigate, packageId, systemId])

	const page = (
		<>
			{!hasSystemId ? (
				<EmptyState message={t('DEPLOYMENT_PACKAGES_NO_SYSTEM')} />
			) : isError ? (
				<EmptyState message={t('DEPLOYMENT_PACKAGES_LOAD_FAILED')} />
			) : packageId ? (
				<PackageDetail
					packageId={packageId}
					systemId={systemId as number}
					onBack={() => navigate(getDeploymentPackagePath())}
				/>
			) : (
				<PackageList
					packages={packages}
					onCreate={() => setCreateOpen(true)}
					onOpen={(id) => navigate(getDeploymentPackagePath(id))}
				/>
			)}
			<CreatePackageDialog
				key={systemId ?? 'none'}
				open={createOpen}
				systemId={systemId}
				onClose={() => setCreateOpen(false)}
				onCreated={(id) => navigate(getDeploymentPackagePath(id))}
			/>
		</>
	)

	return (
		<Box
			data-testid="deployment-packages-page"
			sx={{ display: 'flex', height: '100%', minHeight: 0, overflow: 'hidden', position: 'relative' }}
		>
			<Loader loaded={!isLoading} $absolute />
			{packageId && hasSystemId && !isError ? (
				<ResizableSidebarLayout
					defaultWidth={300}
					localStorageKey="mdd-sidebar-width-deployment-packages"
					content={<Box sx={{ display: 'flex', height: '100%', minHeight: 0, minWidth: 0 }}>{page}</Box>}
				>
					<Box sx={{ display: 'flex', height: '100%', minHeight: 0, overflow: 'hidden' }}>
						<TreePanelV2 />
					</Box>
				</ResizableSidebarLayout>
			) : (
				page
			)}
		</Box>
	)
}

const EmptyState = ({ message }: { message: string }) => (
	<Box
		sx={{
			alignItems: 'center',
			color: '#A8B4B8',
			display: 'flex',
			height: '100%',
			justifyContent: 'center',
			px: 3,
		}}
	>
		<Typography sx={{ fontSize: 13 }}>{message}</Typography>
	</Box>
)
